import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../services/api';
import RecruiterLetterViewer from './RecruiterLetterViewer';

const DEFAULT_EXTENSION_TEMPLATE = `Reference is made to your letter Ref. No. OUK/HR/46/Vol.1 dated 11th March 2026 engaging you as a Student Recruiter for a period of three (3) months. Noting that the period of engagement lapsed on 30th June 2026, I am pleased to inform you that your engagement has been extended for a further period of six (6) months with effect from 1st July 2026 to support the OUK "100,000 Strong" Enrollment Acceleration Plan.

All the other terms of engagement remain unchanged as previously issued to you in the first offer of engagement.

This engagement shall automatically lapse at the end of the period unless otherwise agreed in writing and depending on your performance.`;

const PLACEHOLDERS = [
  { label: 'Full Name',     tag: '{full_name}' },
  { label: 'ID / Passport', tag: '{id_passport}' },
  { label: 'Email',         tag: '{email}' },
  { label: 'Referral Code', tag: '{referral_code}' },
  { label: 'County',        tag: '{county}' },
  { label: 'Mobile No.',    tag: '{mobile_number}' },
  { label: 'Ref No.',       tag: '{ref_no}' },
  { label: 'Date',          tag: '{date}' },
  { label: 'Salutation',    tag: '{salutation}' },
];

const LettersManagement = () => {
  const [activeTab, setActiveTab]   = useState('compose');
  const [letters, setLetters]       = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [message, setMessage]       = useState({ type: '', text: '' });

  // Form state
  const [title, setTitle]                   = useState('');
  const [subject, setSubject]               = useState('EXTENSION OF TEMPORARY ENGAGEMENT AS A STUDENT RECRUITER');
  const [refPrefix, setRefPrefix]           = useState('OUK/HR/46/Vol.1');
  const [officeHeading, setOfficeHeading]   = useState('OFFICE OF THE VICE-CHANCELLOR');
  const [issueDate, setIssueDate]           = useState(new Date().toISOString().split('T')[0]);
  const [signatoryName, setSignatoryName]   = useState('PROF. ELIJAH OMWENGA');
  const [signatoryTitle, setSignatoryTitle] = useState('VICE-CHANCELLOR');
  const [signatureImage, setSignatureImage] = useState(null);
  const [sigMode, setSigMode]               = useState('draw'); // 'draw' | 'upload'
  const [isDrawing, setIsDrawing]           = useState(false);
  const canvasRef                           = useRef(null);

  const [ccList, setCcList]                 = useState('Deputy Vice-Chancellor P&I\nDeputy Vice Chancellor, AA\nHoD, Corporate Communication & Marketing');
  const [bodyTemplate, setBodyTemplate]     = useState(DEFAULT_EXTENSION_TEMPLATE);
  const [targetType, setTargetType]         = useState('all');
  const [selectedRecruiterIds, setSelectedRecruiterIds] = useState([]);

  // Recruiter Recipients Filtering & Search
  const [searchQuery, setSearchQuery]       = useState('');
  const [countyFilter, setCountyFilter]     = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter]     = useState('');
  const [sendEmail, setSendEmail]           = useState(true);
  // Dispatch Progress State
  const [dispatchProgress, setDispatchProgress] = useState({
    active: false,
    percent: 0,
    stage: 'saving', // 'saving' | 'delivering' | 'emailing' | 'complete'
    currentCount: 0,
    totalCount: 0,
    title: ''
  });

  const handleDispatchLetter = async (e) => {
    e.preventDefault();
    if (!title || !subject || !bodyTemplate) {
      setMessage({ type: 'error', text: 'Please fill in Title, Subject, and Body Template.' });
      return;
    }
    if (targetType === 'selected' && selectedRecruiterIds.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one recruiter.' });
      return;
    }

    const totalRecipients = targetType === 'all' ? recruiters.length : selectedRecruiterIds.length;
    setLoading(true);
    setMessage({ type: '', text: '' });

    setDispatchProgress({
      active: true,
      percent: 15,
      stage: 'saving',
      currentCount: 0,
      totalCount: totalRecipients,
      title: title
    });

    try {
      const stepTimer = setTimeout(() => {
        setDispatchProgress(prev => ({
          ...prev,
          percent: 50,
          stage: 'delivering',
          currentCount: Math.ceil(totalRecipients * 0.5)
        }));
      }, 400);

      const payload = {
        title,
        subject,
        ref_prefix:      refPrefix,
        office_heading:  officeHeading,
        body_template:   bodyTemplate,
        signatory_name:  signatoryName,
        signatory_title: signatoryTitle,
        signature_image: signatureImage,
        cc_list:         ccList,
        target_type:     targetType,
        recruiter_ids:   selectedRecruiterIds,
        issue_date:      issueDate,
        send_email:      sendEmail,
      };

      const res = await apiCall('/letters', {
        method: 'POST',
        body:   JSON.stringify(payload),
      });

      clearTimeout(stepTimer);

      setDispatchProgress(prev => ({
        ...prev,
        percent: sendEmail ? 85 : 100,
        stage: sendEmail ? 'emailing' : 'complete',
        currentCount: totalRecipients
      }));

      if (sendEmail) {
        await new Promise(r => setTimeout(r, 600));
        setDispatchProgress(prev => ({
          ...prev,
          percent: 100,
          stage: 'complete'
        }));
      }

      await new Promise(r => setTimeout(r, 600));

      setMessage({ type: 'success', text: res.data.message });
      setTitle('');
      fetchDispatchedLetters();
      setActiveTab('history');
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to dispatch letter.' });
    } finally {
      setLoading(false);
      setDispatchProgress(prev => ({ ...prev, active: false }));
    }
  };

  // Extract unique counties and categories dynamically
  const uniqueCounties = Array.from(new Set(recruiters.map(r => r.county).filter(Boolean))).sort();
  const uniqueCategories = Array.from(
    new Set(
      recruiters
        .map(r => r.category_display_name || r.category_name || r.category)
        .filter(Boolean)
    )
  ).sort();

  // Filter recruiters list based on search, county, category, and status dropdown filters
  const filteredRecruiters = recruiters.filter(r => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = !q ||
      (r.full_name && r.full_name.toLowerCase().includes(q)) ||
      (r.referral_code && r.referral_code.toLowerCase().includes(q)) ||
      (r.id_passport && r.id_passport.toLowerCase().includes(q)) ||
      (r.mobile_number && r.mobile_number.includes(q)) ||
      (r.email && r.email.toLowerCase().includes(q));

    const matchesCounty   = !countyFilter   || r.county === countyFilter;
    const matchesCategory = !categoryFilter || (r.category_display_name || r.category_name || r.category) === categoryFilter;
    const matchesStatus   = !statusFilter   || (r.status || 'active') === statusFilter;

    return matchesSearch && matchesCounty && matchesCategory && matchesStatus;
  });

  const selectAllFiltered = () => {
    const filteredIds = filteredRecruiters.map(r => r.id);
    const newSelected = Array.from(new Set([...selectedRecruiterIds, ...filteredIds]));
    setSelectedRecruiterIds(newSelected);
  };

  const deselectAllFiltered = () => {
    const filteredIds = filteredRecruiters.map(r => r.id);
    setSelectedRecruiterIds(selectedRecruiterIds.filter(id => !filteredIds.includes(id)));
  };

  // Signature drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1a1a';
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) {
        setSignatureImage(canvas.toDataURL('image/png'));
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setSignatureImage(null);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Modals
  const [previewLetter, setPreviewLetter]             = useState(null);
  const [selectedLetterDetails, setSelectedLetterDetails] = useState(null);
  const [viewDelivery, setViewDelivery]               = useState(null);

  useEffect(() => {
    fetchDispatchedLetters();
    fetchRecruiters();
  }, []);

  const fetchDispatchedLetters = async () => {
    try {
      const res = await apiCall('/letters');
      setLetters(res.data.letters || []);
    } catch (err) {
      console.error('Error fetching letters:', err);
    }
  };

  const fetchRecruiters = async () => {
    try {
      const res = await apiCall('/recruiters?limit=500');
      setRecruiters(res.data.recruiters || res.data || []);
    } catch (err) {
      console.error('Error fetching recruiters:', err);
    }
  };

  const handleInsertPlaceholder = (tag) => {
    setBodyTemplate((prev) => prev + ' ' + tag);
  };

  const handleLivePreview = () => {
    setPreviewLetter({
      ref_number:      refPrefix,
      issue_date:      issueDate,
      full_name:       'Lekariap Maureen Jepkoech',
      id_passport:     '31482415',
      email:           'maureenlekariap@gmail.com',
      subject,
      body_template:   bodyTemplate,
      office_heading:  officeHeading,
      signatory_name:  signatoryName,
      signatory_title: signatoryTitle,
      signature_image: signatureImage,
      cc_list:         ccList,
    });
  };

  const handleViewDetails = async (letterId) => {
    try {
      const res = await apiCall(`/letters/${letterId}`);
      setSelectedLetterDetails(res.data);
    } catch (err) {
      console.error('Error fetching letter details:', err);
    }
  };

  const handleDeleteLetter = async (letterId, letterTitle) => {
    if (!window.confirm(`Revoke / delete "${letterTitle}"? This cannot be undone.`)) return;
    try {
      await apiCall(`/letters/${letterId}`, { method: 'DELETE' });
      fetchDispatchedLetters();
      if (selectedLetterDetails?.letter?.id === letterId) setSelectedLetterDetails(null);
    } catch (err) {
      console.error('Error deleting letter:', err);
    }
  };

  const toggleRecruiter = (id, checked) => {
    setSelectedRecruiterIds(prev =>
      checked ? [...prev, id] : prev.filter(x => x !== id)
    );
  };

  return (
    <div className="main-content">

      {/* ── Page Header ─────────────────────────────── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Official Recruiter Letters</h1>
          <p className="page-subtitle">
            Generate and issue dynamic official engagement letters to recruiters, matching the OUK official letterhead.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="page-header-actions">
          <div style={styles.tabBar}>
            <button
              onClick={() => setActiveTab('compose')}
              style={activeTab === 'compose' ? { ...styles.tabBtn, ...styles.tabBtnActive } : styles.tabBtn}
            >
              ✏️ Compose Letter
            </button>
            <button
              onClick={() => setActiveTab('history')}
              style={activeTab === 'history' ? { ...styles.tabBtn, ...styles.tabBtnActive } : styles.tabBtn}
            >
              📋 Sent History ({letters.length})
            </button>
          </div>
        </div>
      </div>

      {/* ── Dynamic Dispatch Progress Card ───────────────────────────── */}
      {dispatchProgress.active && (
        <div style={styles.progressCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={styles.spinner} />
              <div>
                <h4 style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#037b90' }}>
                  {dispatchProgress.percent < 100 ? '🚀 Dispatching Official Letter...' : '🎉 Dispatch & Delivery Complete!'}
                </h4>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Letter: <strong>"{dispatchProgress.title}"</strong> ({dispatchProgress.totalCount} recruiter recipients)
                </span>
              </div>
            </div>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#037b90', marginLeft: 'auto' }}>
              {dispatchProgress.percent}%
            </span>
          </div>

          {/* Animated Progress Bar Track */}
          <div style={styles.progressTrack}>
            <div
              style={{
                height: '100%',
                borderRadius: 99,
                width: `${dispatchProgress.percent}%`,
                transition: 'width 0.4s ease-in-out',
                background: dispatchProgress.percent === 100 
                  ? '#10b981' 
                  : 'linear-gradient(90deg, #037b90 0%, #ff7f50 100%)',
              }}
            />
          </div>

          {/* Step Subtext */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginTop: 10, color: 'var(--text-secondary)' }}>
            <span>
              {dispatchProgress.stage === 'saving' && '📝 Step 1/3: Saving master letter template & resolving placeholders…'}
              {dispatchProgress.stage === 'delivering' && `📑 Step 2/3: Generating deliveries for ${dispatchProgress.totalCount} recruiter dashboards…`}
              {dispatchProgress.stage === 'emailing' && '📧 Step 3/3: Enqueuing background email job with attached PDF/HTML copies…'}
              {dispatchProgress.stage === 'complete' && '✅ All letter deliveries and email notification jobs successfully initiated!'}
            </span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              {dispatchProgress.currentCount} / {dispatchProgress.totalCount} recipients
            </span>
          </div>
        </div>
      )}

      {/* ── Alert Banner ───────────────────────────── */}
      {message.text && (
        <div style={message.type === 'success' ? styles.alertSuccess : styles.alertError}>
          <span>{message.text}</span>
          <button onClick={() => setMessage({ type: '', text: '' })} style={styles.alertClose}>✕</button>
        </div>
      )}

      {/* ══ TAB: COMPOSE ═══════════════════════════ */}
      {activeTab === 'compose' && (
        <form onSubmit={handleDispatchLetter}>

          {/* ── Section: Basic Info ─────────────────── */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardHeaderIcon}>📝</span>
              <span style={styles.cardHeaderTitle}>Letter Details</span>
            </div>
            <div style={styles.cardBody}>
              <div style={styles.formGrid2}>

                <div className="form-group">
                  <label className="form-label">
                    Letter Title <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="e.g. Engagement Extension Letter – July 2026"
                    required
                  />
                  <span style={styles.fieldHint}>Internal title used for tracking</span>
                </div>

                <div className="form-group">
                  <label className="form-label">Reference Prefix</label>
                  <input
                    className="form-input"
                    type="text"
                    value={refPrefix}
                    onChange={e => setRefPrefix(e.target.value)}
                    placeholder="OUK/HR/46/Vol.1"
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">
                    Office / Department Heading <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    value={officeHeading}
                    onChange={e => setOfficeHeading(e.target.value)}
                    placeholder="OFFICE OF THE VICE-CHANCELLOR"
                    style={{ textTransform: 'uppercase', fontWeight: 600 }}
                    required
                  />
                  <span style={styles.fieldHint}>Dynamic statement displayed below the OUK logo on the letterhead</span>
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">
                    Subject Line (RE:) <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    style={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.02em' }}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Issue Date</label>
                  <input
                    className="form-input"
                    type="date"
                    value={issueDate}
                    onChange={e => setIssueDate(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Recruiter Recipients</label>
                  <select
                    className="form-select"
                    value={targetType}
                    onChange={e => { setTargetType(e.target.value); setSelectedRecruiterIds([]); }}
                  >
                    <option value="all">All Active Recruiters ({recruiters.length})</option>
                    <option value="selected">Select Specific Recruiters…</option>
                  </select>
                </div>
              </div>

              {/* Recruiter multi-select & Filtering */}
              {targetType === 'selected' && (
                <div style={styles.recruiterBox}>

                  {/* Filter Toolbar */}
                  <div style={{ padding: '12px 16px', background: '#fafbfc', borderBottom: '1px solid var(--border-color)', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                    
                    {/* Search Input */}
                    <div style={{ flex: '1 1 180px' }}>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="🔍 Search name, code, ID, phone, email..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{ fontSize: 13, height: 36, padding: '6px 12px', background: '#fff' }}
                      />
                    </div>

                    {/* Category Filter Dropdown */}
                    <div style={{ minWidth: 160 }}>
                      <select
                        className="form-select"
                        value={categoryFilter}
                        onChange={e => setCategoryFilter(e.target.value)}
                        style={{ fontSize: 13, height: 36, padding: '6px 12px', background: '#fff', cursor: 'pointer' }}
                      >
                        <option value="">All Categories ({uniqueCategories.length})</option>
                        {uniqueCategories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    {/* County Filter Dropdown */}
                    <div style={{ minWidth: 150 }}>
                      <select
                        className="form-select"
                        value={countyFilter}
                        onChange={e => setCountyFilter(e.target.value)}
                        style={{ fontSize: 13, height: 36, padding: '6px 12px', background: '#fff', cursor: 'pointer' }}
                      >
                        <option value="">All Counties ({uniqueCounties.length})</option>
                        {uniqueCounties.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    {/* Status Filter Dropdown */}
                    <div style={{ minWidth: 130 }}>
                      <select
                        className="form-select"
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        style={{ fontSize: 13, height: 36, padding: '6px 12px', background: '#fff', cursor: 'pointer' }}
                      >
                        <option value="">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    {/* Quick Selection Buttons */}
                    <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                      <button
                        type="button"
                        onClick={selectAllFiltered}
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: 11, padding: '4px 10px', height: 36 }}
                      >
                        Select Filtered ({filteredRecruiters.length})
                      </button>
                      <button
                        type="button"
                        onClick={deselectAllFiltered}
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: 11, padding: '4px 10px', height: 36 }}
                      >
                        Deselect Filtered
                      </button>
                    </div>

                  </div>

                  <div style={styles.recruiterBoxHeader}>
                    <span>Showing {filteredRecruiters.length} of {recruiters.length} recruiters</span>
                    <span style={styles.selectionBadge}>{selectedRecruiterIds.length} selected</span>
                  </div>

                  <div style={styles.recruiterGrid}>
                    {filteredRecruiters.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, gridColumn: '1 / -1', background: '#fff' }}>
                        No recruiters match the current search or filter criteria.
                      </div>
                    ) : (
                      filteredRecruiters.map(r => (
                        <label key={r.id} style={styles.recruiterItem}>
                          <input
                            type="checkbox"
                            checked={selectedRecruiterIds.includes(r.id)}
                            onChange={e => toggleRecruiter(r.id, e.target.checked)}
                            style={{ accentColor: '#037b90', marginRight: 8, flexShrink: 0 }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={styles.recruiterName}>{r.full_name}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                              {r.category_display_name || r.category_name || r.category || 'General'} • {r.county || 'N/A'} • ID: {r.id_passport || 'N/A'}
                            </div>
                          </div>
                          <span style={styles.recruiterCode}>{r.referral_code}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Section: Body Template ──────────────── */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardHeaderIcon}>🔖</span>
              <span style={styles.cardHeaderTitle}>Dynamic Body Content</span>
              <button
                type="button"
                onClick={() => setBodyTemplate(DEFAULT_EXTENSION_TEMPLATE)}
                style={styles.resetLink}
              >
                Reset to Standard Template
              </button>
            </div>
            <div style={styles.cardBody}>

              {/* Placeholder toolbar */}
              <div style={styles.placeholderToolbar}>
                <span style={styles.placeholderLabel}>Insert placeholder →</span>
                <div style={styles.placeholderChips}>
                  {PLACEHOLDERS.map(p => (
                    <button
                      key={p.tag}
                      type="button"
                      onClick={() => handleInsertPlaceholder(p.tag)}
                      style={styles.chip}
                      title={`Insert ${p.tag}`}
                    >
                      {p.tag}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                className="form-textarea"
                rows={9}
                value={bodyTemplate}
                onChange={e => setBodyTemplate(e.target.value)}
                style={{ fontFamily: "'Times New Roman', serif", lineHeight: 1.7, marginTop: 12 }}
                required
              />
            </div>
          </div>

          {/* ── Section: Signatory ──────────────────── */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardHeaderIcon}>✍️</span>
              <span style={styles.cardHeaderTitle}>Signatory & CC Information</span>
            </div>
            <div style={styles.cardBody}>
              <div style={styles.formGrid3}>
                <div className="form-group">
                  <label className="form-label">Signatory Name</label>
                  <input
                    className="form-input"
                    type="text"
                    value={signatoryName}
                    onChange={e => setSignatoryName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Signatory Title</label>
                  <input
                    className="form-input"
                    type="text"
                    value={signatoryTitle}
                    onChange={e => setSignatoryTitle(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Copy To (CC List)</label>
                  <textarea
                    className="form-textarea"
                    rows={3}
                    value={ccList}
                    onChange={e => setCcList(e.target.value)}
                    style={{ fontSize: 12 }}
                  />
                </div>
              </div>

              {/* ── Signature Drawer / Uploader ── */}
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                  <label className="form-label" style={{ margin: 0, fontWeight: 600 }}>
                    ✍️ Official Signature
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setSigMode('draw')}
                      className={`btn btn-sm ${sigMode === 'draw' ? 'btn-primary' : 'btn-outline'}`}
                    >
                      ✏️ Draw Signature
                    </button>
                    <button
                      type="button"
                      onClick={() => setSigMode('upload')}
                      className={`btn btn-sm ${sigMode === 'upload' ? 'btn-primary' : 'btn-outline'}`}
                    >
                      📁 Upload Image
                    </button>
                    {signatureImage && (
                      <button
                        type="button"
                        onClick={clearSignature}
                        className="btn btn-danger btn-sm"
                      >
                        ✕ Clear
                      </button>
                    )}
                  </div>
                </div>

                {sigMode === 'draw' ? (
                  <div style={{ border: '2px dashed #037b90', borderRadius: 8, padding: 10, background: '#fafbfc', display: 'inline-block', maxWidth: '100%' }}>
                    <canvas
                      ref={canvasRef}
                      width={400}
                      height={110}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      style={{ border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'crosshair', background: '#ffffff', width: '100%', maxWidth: '400px', height: '110px' }}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 4 }}>
                      Draw signature in box using mouse or touch
                    </div>
                  </div>
                ) : (
                  <div style={{ maxWidth: 400 }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="form-input"
                      style={{ fontSize: 12 }}
                    />
                    <span style={styles.fieldHint}>Upload signature PNG/JPG image with transparent or white background</span>
                  </div>
                )}

                {signatureImage && (
                  <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, background: '#f8fafc', padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', width: 'fit-content' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#037b90' }}>Attached Signature:</span>
                    <img src={signatureImage} alt="Signature Preview" style={{ maxHeight: 45, maxWidth: 180, objectFit: 'contain' }} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Section: Email Dispatch Option ─────── */}
          <div style={{ padding: '16px 20px', background: sendEmail ? '#f0fdfc' : '#f8fafc', border: `1px solid ${sendEmail ? '#b2dfdb' : 'var(--border-color)'}`, borderRadius: 12, marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flex: 1, minWidth: 260 }}>
              <input
                type="checkbox"
                id="sendEmailCheckbox"
                checked={sendEmail}
                onChange={e => setSendEmail(e.target.checked)}
                style={{ width: 20, height: 20, accentColor: '#037b90', cursor: 'pointer', marginTop: 2 }}
              />
              <div>
                <label htmlFor="sendEmailCheckbox" style={{ fontWeight: 700, fontSize: 14, color: sendEmail ? '#037b90' : 'var(--text-primary)', cursor: 'pointer', display: 'block', marginBottom: 2 }}>
                  📧 Email Notification &amp; Attachment Job
                </label>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4, display: 'block' }}>
                  When enabled, dispatching this letter triggers a background email job that delivers an email notification with the printable official letter attached directly to each recruiter's email inbox.
                </span>
              </div>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 99, background: sendEmail ? '#d1fae5' : '#e2e8f0', color: sendEmail ? '#065f46' : '#64748b', whiteSpace: 'nowrap' }}>
              {sendEmail ? '⚡ Email Job Enabled' : 'Dashboard Only'}
            </span>
          </div>

          {/* ── Action Row ──────────────────────────── */}
          <div style={styles.actionRow}>
            <button
              type="button"
              onClick={handleLivePreview}
              className="btn btn-outline btn-lg"
              style={{ gap: 8 }}
            >
              👁️ Live PDF Preview
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-lg"
              style={{ gap: 8 }}
            >
              {loading ? '⏳ Dispatching…' : '🚀 Dispatch to Recruiter Dashboards'}
            </button>
          </div>
        </form>
      )}

      {/* ══ TAB: HISTORY ═══════════════════════════ */}
      {activeTab === 'history' && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardHeaderIcon}>📋</span>
            <span style={styles.cardHeaderTitle}>Sent Letters History</span>
          </div>
          <div className="table-container" style={{ borderRadius: 0, boxShadow: 'none', border: 'none' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Ref Prefix</th>
                  <th>Subject</th>
                  <th>Recipients</th>
                  <th>Date Issued</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {letters.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '48px 16px', color: '#a0aec0' }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                      No letters have been issued yet.
                    </td>
                  </tr>
                ) : (
                  letters.map(l => (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 600 }}>{l.title}</td>
                      <td>
                        <span style={styles.monoTag}>{l.ref_prefix}</span>
                      </td>
                      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.subject}
                      </td>
                      <td>
                        <span style={styles.recipientBadge}>{l.recipient_count || 0} recruiters</span>
                      </td>
                      <td style={{ color: '#718096' }}>
                        {new Date(l.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => handleViewDetails(l.id)}
                          className="btn btn-outline btn-sm"
                          style={{ marginRight: 8 }}
                        >
                          View Recipients
                        </button>
                        <button
                          onClick={() => handleDeleteLetter(l.id, l.title)}
                          className="btn btn-danger btn-sm"
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Live Preview Modal ───────────────────── */}
      {previewLetter && (
        <RecruiterLetterViewer
          letter={previewLetter}
          onClose={() => setPreviewLetter(null)}
        />
      )}

      {/* ── Recipients Detail Modal ──────────────── */}
      {selectedLetterDetails && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>{selectedLetterDetails.letter?.title}</div>
                <div style={styles.modalSubtitle}>Ref: {selectedLetterDetails.letter?.ref_prefix}</div>
              </div>
              <button onClick={() => setSelectedLetterDetails(null)} style={styles.closeBtn}>✕</button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.modalSectionLabel}>
                Deliveries — {selectedLetterDetails.deliveries?.length || 0} recruiter(s)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {selectedLetterDetails.deliveries?.map(d => (
                  <div key={d.id} style={styles.deliveryRow}>
                    <div>
                      <div style={styles.deliveryName}>{d.full_name}</div>
                      <div style={styles.deliveryMeta}>
                        Ref: {d.ref_number} &nbsp;|&nbsp; Code: {d.referral_code} &nbsp;|&nbsp; ID: {d.id_passport}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={d.status === 'viewed' ? styles.badgeViewed : styles.badgeNew}>
                        {d.status === 'viewed' ? 'Viewed' : 'Unread'}
                      </span>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setViewDelivery(d)}
                      >
                        View Letter
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Per-delivery Letter Preview ──────────── */}
      {viewDelivery && (
        <RecruiterLetterViewer
          letter={viewDelivery}
          onClose={() => setViewDelivery(null)}
        />
      )}
    </div>
  );
};

/* ── Inline styles ─────────────────────────────────── */
const styles = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },

  pageHeader: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start',
    justifyContent: 'space-between', gap: 16,
    paddingBottom: 20, borderBottom: '1px solid var(--border-color)',
  },
  pageTitleRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  pageIcon: { fontSize: 22 },
  pageTitle: { fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  pageSubtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: 0 },

  tabBar: {
    display: 'flex', background: '#f1f5f9',
    padding: 4, borderRadius: 8, gap: 4,
  },
  tabBtn: {
    padding: '8px 18px', border: 'none', borderRadius: 6,
    fontSize: 13, fontWeight: 500, cursor: 'pointer',
    background: 'transparent', color: 'var(--text-secondary)',
    fontFamily: 'Poppins, sans-serif', transition: 'all 0.2s',
  },
  tabBtnActive: {
    background: '#fff', color: 'var(--primary-color)',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)', fontWeight: 600,
  },

  alertSuccess: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', background: '#ecfdf5', border: '1px solid #6ee7b7',
    borderRadius: 8, color: '#065f46', fontSize: 13, fontWeight: 500,
  },
  alertError: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 8, color: '#991b1b', fontSize: 13, fontWeight: 500,
  },
  alertClose: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontWeight: 700, fontSize: 14, marginLeft: 12, color: 'inherit',
  },

  progressCard: {
    background: 'linear-gradient(135deg, #f0fdff 0%, #f8fafc 100%)',
    border: '1.5px solid #b2e8f0',
    borderRadius: 14,
    padding: '18px 22px',
    boxShadow: '0 4px 24px rgba(3,123,144,0.08)',
  },
  progressTrack: {
    width: '100%',
    height: 10,
    borderRadius: 99,
    background: '#e2e8f0',
    overflow: 'hidden',
    marginTop: 4,
  },
  spinner: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: '3px solid #b2e8f0',
    borderTopColor: '#037b90',
    animation: 'spin 0.8s linear infinite',
    flexShrink: 0,
  },

  card: {
    background: '#fff', borderRadius: 12,
    border: '1px solid var(--border-color)', overflow: 'hidden',
    boxShadow: 'var(--shadow-sm)', marginBottom: 24,
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '14px 20px', background: '#fafbfc',
    borderBottom: '1px solid var(--border-color)',
  },
  cardHeaderIcon: { fontSize: 16 },
  cardHeaderTitle: { fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', flex: 1 },
  cardBody: { padding: '20px 20px' },

  formGrid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 },
  formGrid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 },

  fieldHint: { fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'block' },

  recruiterBox: {
    marginTop: 16, border: '1px solid var(--border-color)',
    borderRadius: 8, overflow: 'hidden',
  },
  recruiterBoxHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', background: '#f8fafc',
    fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-color)',
  },
  selectionBadge: {
    background: 'var(--primary-color)', color: '#fff',
    borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 600,
  },
  recruiterGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 1, maxHeight: 220, overflowY: 'auto', background: 'var(--border-color)',
  },
  recruiterItem: {
    display: 'flex', alignItems: 'center', gap: 0,
    padding: '8px 14px', background: '#fff', cursor: 'pointer',
    fontSize: 13,
  },
  recruiterName: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  recruiterCode: {
    marginLeft: 6, fontSize: 11, color: 'var(--primary-color)',
    background: '#e0f7fa', borderRadius: 4, padding: '1px 6px',
  },

  placeholderToolbar: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center',
    gap: 10, padding: '10px 14px',
    background: '#f0fdfc', border: '1px solid #b2dfdb',
    borderRadius: 8,
  },
  placeholderLabel: { fontSize: 11, fontWeight: 600, color: '#037b90', whiteSpace: 'nowrap' },
  placeholderChips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: {
    padding: '4px 10px', border: '1px solid #80cbc4',
    background: '#fff', borderRadius: 4,
    fontSize: 11, fontFamily: 'monospace', fontWeight: 600,
    color: '#037b90', cursor: 'pointer',
    transition: 'all 0.15s',
  },
  resetLink: {
    background: 'none', border: 'none', color: 'var(--primary-color)',
    fontSize: 12, fontWeight: 500, cursor: 'pointer',
    textDecoration: 'underline', fontFamily: 'Poppins, sans-serif',
  },

  actionRow: {
    display: 'flex', justifyContent: 'flex-end',
    gap: 12, paddingTop: 4,
  },

  monoTag: {
    fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
    color: '#037b90', background: '#e0f7fa',
    padding: '2px 8px', borderRadius: 4,
  },
  recipientBadge: {
    background: '#e0f7fa', color: '#007b90',
    padding: '3px 10px', borderRadius: 99,
    fontSize: 11, fontWeight: 600,
  },

  // Modals
  overlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.55)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 14, boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
    maxWidth: 720, width: '100%', maxHeight: '85vh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 22px', background: '#fafbfc',
    borderBottom: '1px solid var(--border-color)',
  },
  modalTitle:    { fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' },
  modalSubtitle: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 18, color: 'var(--text-secondary)', padding: 4,
    borderRadius: 6, lineHeight: 1,
  },
  modalBody: { padding: '20px 22px', overflowY: 'auto', flex: 1 },
  modalSectionLabel: {
    fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: 12,
  },

  deliveryRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', background: '#f8fafc',
    border: '1px solid var(--border-color)', borderRadius: 8,
  },
  deliveryName: { fontWeight: 600, fontSize: 13, marginBottom: 2 },
  deliveryMeta: { fontSize: 11, color: 'var(--text-secondary)' },

  badgeViewed: {
    fontSize: 11, fontWeight: 600, padding: '3px 10px',
    borderRadius: 99, background: '#f1f5f9', color: '#64748b',
  },
  badgeNew: {
    fontSize: 11, fontWeight: 600, padding: '3px 10px',
    borderRadius: 99, background: '#d1fae5', color: '#065f46',
  },
};

export default LettersManagement;
